import type * as React from 'react'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
} from '@/components/inspections'
import { CardContent, CardFooter } from '@/components/ui/card'

interface AdminCardPageProps {
	/** Icon node rendered inside the header icon box */
	icon: React.ReactNode
	title: string
	subtitle?: string
	/** Adds an ArrowLeft back button to the header */
	onBack?: () => void
	/** Slot rendered on the right side of the header (e.g. a "+  New" button) */
	actions?: React.ReactNode
	/**
	 * Text displayed in the `<CardFooter>` count row.
	 * Omit to hide the footer entirely.
	 */
	footerText?: string
	/**
	 * Custom node rendered on the right side of the footer (e.g. a save button).
	 * Can be used together with or instead of `footerText`.
	 */
	footerActions?: React.ReactNode
	/**
	 * Modal node (e.g. `<ResponsiveFormModal>`) rendered inside the Card so it
	 * stays associated with the page's JSX tree. Because Dialog/Drawer use
	 * portals the actual DOM position has no visual effect.
	 */
	modal?: React.ReactNode
	children: React.ReactNode
}

/**
 * Standard shell for administration list pages:
 * InspectionCard (mediumGrey) → InspectionCardHeader → CardContent → CardFooter.
 *
 * Keeps page files focused purely on their own CRUD logic.
 */
export function AdminCardPage({
	icon,
	title,
	subtitle,
	onBack,
	actions,
	footerText,
	footerActions,
	modal,
	children,
}: AdminCardPageProps) {
	const hasFooter = footerText || footerActions
	return (
		<InspectionCard colors={INSPECTION_COLORS.mediumGrey}>
			<InspectionCardHeader
				colors={INSPECTION_COLORS.mediumGrey}
				icon={icon}
				title={title}
				subtitle={subtitle}
				onBack={onBack}
				actions={actions}
			/>

			<CardContent className="flex flex-col gap-4 px-4 py-5 sm:px-6">
				{children}
			</CardContent>
			{modal}

			{hasFooter && (
				<CardFooter className="sticky bottom-0 bg-white sm:static sm:flex  border-t border-kaefer-medium-grey/20 px-4 py-3 sm:px-6 z-10 justify-between items-center gap-3">
					{footerText ? (
						<p className="text-xs text-muted-foreground">{footerText}</p>
					) : (
						<span />
					)}
					{footerActions && <div className="flex gap-3">{footerActions}</div>}
				</CardFooter>
			)}
		</InspectionCard>
	)
}
